/* ProPlay motion layer. Progressive enhancement only: every page works without it. */
(() => {
  if (window.__ppMotion) return;
  window.__ppMotion = true;

  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const io = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 })
    : null;
  const watch = (el) => { if (io) io.observe(el); else el.classList.add('is-in'); };

  const LACE = '<svg class="ppm-lace" viewBox="0 0 240 18" aria-hidden="true" focusable="false">' +
    '<path class="ppm-lace-rope" pathLength="1" d="M2 9C22 0 42 18 62 9S102 0 122 9S162 18 182 9S222 0 238 9"/>' +
    '<path class="ppm-lace-x" d="M28 3l8 12M36 3l-8 12M88 3l8 12M96 3l-8 12M148 3l8 12M156 3l-8 12M208 3l8 12M216 3l-8 12"/></svg>';

  /* Countdown to the start of December 2026, New York time. */
  const LAUNCH_MONTH = new Date('2026-12-01T00:00:00-05:00').getTime();

  function initCountdown(root) {
    const stack = root.querySelector('.ppb-line .ppb-stack');
    if (!stack || stack.querySelector('.ppm-count')) return;

    const box = document.createElement('div');
    box.className = 'ppm-count';
    const label = document.createElement('p');
    label.className = 'ppm-count-k';
    label.textContent = 'Countdown to December 2026';
    const row = document.createElement('div');
    row.className = 'ppm-count-row';
    row.setAttribute('role', 'timer');
    row.setAttribute('aria-label', 'Time left until December 2026');
    const cells = {};
    for (const [key, text] of [['d', 'Days'], ['h', 'Hours'], ['m', 'Min'], ['s', 'Sec']]) {
      const cell = document.createElement('span');
      cell.className = 'ppm-count-cell';
      const num = document.createElement('b');
      num.textContent = '00';
      const small = document.createElement('small');
      small.textContent = text;
      cell.append(num, small);
      row.append(cell);
      cells[key] = num;
    }
    box.append(label, row);
    const ctas = stack.querySelector('.ppb-ctas');
    if (ctas) ctas.before(box); else stack.append(box);

    const pad = (n) => String(n).padStart(2, '0');
    const set = (el, value) => {
      if (el.textContent === value) return;
      el.textContent = value;
      if (reduce.matches) return;
      el.classList.remove('is-flip');
      void el.offsetWidth;
      el.classList.add('is-flip');
    };

    let timer;
    const tick = () => {
      const left = LAUNCH_MONTH - Date.now();
      if (left <= 0) {
        window.clearInterval(timer);
        const done = document.createElement('p');
        done.className = 'ppm-count-done';
        done.textContent = 'December is here \u2014 launch month for the ProPlay glove.';
        label.remove();
        row.replaceWith(done);
        return;
      }
      const total = Math.floor(left / 1000);
      set(cells.d, pad(Math.floor(total / 86400)));
      set(cells.h, pad(Math.floor((total % 86400) / 3600)));
      set(cells.m, pad(Math.floor((total % 3600) / 60)));
      set(cells.s, pad(total % 60));
    };
    tick();
    timer = window.setInterval(tick, 1000);
  }

  function enhanceBallpark(root) {
    if (!root || root.dataset.ppmReady) return;
    root.dataset.ppmReady = '1';

    for (const heading of root.querySelectorAll('.ppb-h2')) {
      heading.insertAdjacentHTML('afterend', LACE);
      watch(heading.nextElementSibling);
    }

    root.querySelectorAll('.ppb-card').forEach((card, index) => {
      const media = card.querySelector('.ppb-card-img');
      if (media && !media.querySelector('.ppm-peek')) {
        const peek = document.createElement('span');
        peek.className = 'ppm-peek';
        peek.setAttribute('aria-hidden', 'true');
        const specs = document.createElement('span');
        specs.textContent = (card.querySelector('.ppb-specs')?.textContent || '').trim() || 'See the details';
        const cta = document.createElement('b');
        cta.textContent = 'View glove \u2192';
        peek.append(specs, cta);
        media.append(peek);
      }
      if (!reduce.matches) {
        card.classList.add('ppm-reveal');
        card.style.setProperty('--ppm-d', `${(index % 4) * 90}ms`);
        watch(card);
      }
    });

    root.querySelectorAll('.ppb-bor').forEach((tile, index) => {
      if (reduce.matches) return;
      tile.classList.add('ppm-reveal');
      tile.style.setProperty('--ppm-d', `${index * 70}ms`);
      watch(tile);
    });

    const card = root.querySelector('.ppb-rescard');
    if (card) watch(card);

    initCountdown(root);

    for (const button of root.querySelectorAll('[data-set]')) {
      button.addEventListener('click', () => {
        if (button.getAttribute('data-set') !== 'night' || reduce.matches) return;
        root.classList.remove('ppm-lights-on');
        void root.offsetWidth;
        root.classList.add('ppm-lights-on');
        window.setTimeout(() => root.classList.remove('ppm-lights-on'), 1300);
      });
    }
  }

  function initPocketPop() {
    const counts = [...document.querySelectorAll('.pph-count')];
    if (!counts.length) return;
    const read = () => parseInt(counts[0].textContent, 10) || 0;
    let last = read();
    const pop = () => {
      if (reduce.matches) return;
      for (const bag of document.querySelectorAll('.pph-bag')) {
        const ball = document.createElement('span');
        ball.className = 'ppm-ball';
        ball.setAttribute('aria-hidden', 'true');
        bag.append(ball);
        bag.classList.remove('ppm-pop');
        void bag.offsetWidth;
        bag.classList.add('ppm-pop');
        window.setTimeout(() => { ball.remove(); bag.classList.remove('ppm-pop'); }, 1100);
      }
    };
    const observer = new MutationObserver(() => {
      const now = read();
      if (now > last) pop();
      last = now;
    });
    for (const count of counts) observer.observe(count, { childList: true, characterData: true, subtree: true });
  }

  function initSpin() {
    const spin = document.querySelector('[data-ppm-spin]');
    if (!spin || spin.dataset.ready) return;
    spin.dataset.ready = '1';
    const main = document.querySelector('main > .shopify-section[id*="__main"]');
    if (main) main.after(spin);

    const stage = spin.querySelector('.ppm-spin-stage');
    const frames = [...stage.querySelectorAll('.ppm-spin-frame')];
    if (frames.length < 2) return;
    let current = 0;
    const show = (n) => {
      current = ((n % frames.length) + frames.length) % frames.length;
      frames.forEach((frame, i) => frame.classList.toggle('is-active', i === current));
      stage.setAttribute('aria-valuenow', String(current + 1));
      stage.setAttribute('aria-valuetext', `Angle ${current + 1} of ${frames.length}`);
    };

    let startX = null;
    let startFrame = 0;
    stage.addEventListener('pointerdown', (e) => {
      startX = e.clientX;
      startFrame = current;
      stage.setPointerCapture(e.pointerId);
    });
    stage.addEventListener('pointermove', (e) => {
      if (startX === null) return;
      const step = Math.max(4, stage.clientWidth / frames.length / 1.4);
      show(startFrame + Math.round((e.clientX - startX) / step));
    });
    const end = () => { startX = null; };
    stage.addEventListener('pointerup', end);
    stage.addEventListener('pointercancel', end);
    stage.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); show(current + 1); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); show(current - 1); }
    });

    if (!reduce.matches && io) {
      const teaser = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        teaser.disconnect();
        let i = 0;
        const timer = window.setInterval(() => {
          if (startX !== null || i >= frames.length) { window.clearInterval(timer); return; }
          show(current + 1);
          i += 1;
        }, 1400 / frames.length);
      }, { threshold: 0.5 });
      teaser.observe(stage);
    }
  }

  function init() {
    document.querySelectorAll('.ppb').forEach(enhanceBallpark);
    initPocketPop();
    initSpin();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', () => document.querySelectorAll('.ppb').forEach(enhanceBallpark));
})();